// src/resources/verifiedUsers.js

import React, { useState } from 'react';
import { 
    List, 
    Datagrid, 
    TextField, 
    EmailField, 
    DateField, 
    ShowButton,
    Show,
    SimpleShowLayout,
    FunctionField,
    TextInput,
    Title,
    useRecordContext,
    useDataProvider,
    useNotify,
    useRefresh,
    Confirm // مكون جاهز لرسائل التأكيد
} from 'react-admin';
import { Typography, Box, IconButton, Tooltip } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Cancel'; // أيقونة X

// Search filter component
const userFilters = [
    <TextInput source="q" label="Search by Name or Email" alwaysOn />,
];

// --- [تعديل] مكون ImageBox المتطور (مع زر الحذف) ---
const ImageBox = ({ title, source, directUrl, recordId, fieldName }) => {
    const record = useRecordContext();
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const refresh = useRefresh();
    
    // حالة لفتح/غلق نافذة التأكيد
    const [openConfirm, setOpenConfirm] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    // تحديد الرابط: إما مباشر (للكائنات المتداخلة) أو من السجل
    const imageUrl = directUrl || (record && source ? record[source] : null);
    
    // لو مفيش صورة، نعرض نص بديل
    if (!imageUrl) {
        return (
            <Box textAlign="center" mb={2} style={{ margin: '10px', border: '1px solid #eee', padding: '10px', borderRadius: '8px', minWidth: '200px' }}>
                <Typography variant="subtitle2" gutterBottom>{title}</Typography>
                <Typography variant="caption" color="textSecondary" style={{ fontStyle: 'italic' }}>
                    Deleted / Not Provided
                </Typography>
            </Box>
        );
    }

    // دالة فتح نافذة التأكيد
    const handleDeleteClick = () => setOpenConfirm(true);
    const handleDialogClose = () => setOpenConfirm(false);

    // دالة تنفيذ الحذف الفعلي
    const handleConfirmDelete = async () => {
        setIsLoading(true);
        try {
            // نستخدم dataProvider.create لإرسال طلب POST
            await dataProvider.create(`users/${recordId || record.id}/delete-image`, {
                data: { fieldName: fieldName || source } // نرسل اسم الحقل (مثلاً idFrontImageUrl)
            });
            notify('Image permanently deleted.', { type: 'success' });
            refresh(); // تحديث الصفحة لإخفاء الصورة
        } catch (error) {
            notify(`Error: ${error.message}`, { type: 'warning' });
        } finally {
            setIsLoading(false);
            setOpenConfirm(false);
        }
    };

    return (
        <Box position="relative" textAlign="center" mb={2} style={{ margin: '10px', border: '1px solid #ddd', padding: '10px', borderRadius: '8px' }}>
            <Typography variant="subtitle2" gutterBottom>{title}</Typography>
            
            {/* عرض الصورة */}
            <a href={imageUrl} target="_blank" rel="noopener noreferrer">
                <img src={imageUrl} alt={title} style={{ maxWidth: '250px', maxHeight: '250px', objectFit: 'contain', display: 'block' }} />
            </a>

            {/* زر الحذف (X) يظهر فوق الصورة */}
            <Tooltip title="Delete Permanently">
                <IconButton 
                    onClick={handleDeleteClick}
                    style={{ 
                        position: 'absolute', 
                        top: 5, 
                        right: 5, 
                        backgroundColor: 'rgba(255,255,255,0.8)', 
                        color: 'red' 
                    }}
                    size="small"
                >
                    <DeleteIcon />
                </IconButton>
            </Tooltip>

            {/* نافذة التأكيد */}
            <Confirm
                isOpen={openConfirm}
                loading={isLoading}
                title={`Delete ${title}?`}
                content="Are you sure you want to permanently delete this image from the server? This action cannot be undone."
                onConfirm={handleConfirmDelete}
                onClose={handleDialogClose}
                confirmColor="warning"
                ConfirmIcon={DeleteIcon}
            />
        </Box>
    );
};

// This component defines the list of verified users
export const VerifiedUserList = (props) => (
    <List {...props} filters={userFilters} title="Verified Users">
        <Datagrid rowClick="show">
            <TextField source="name" />
            <EmailField source="email" />
            <TextField source="phone" />
            <FunctionField 
                label="Car Status" 
                render={record => (record.car && record.car.isVerified) ? "✅ Verified Car" : "No Car / Unverified"} 
            />
            <DateField source="joinDate" />
            <ShowButton />
        </Datagrid>
    </List>
);

// This component defines the details/show page for a verified user
export const VerifiedUserShow = (props) => (
    <Show {...props} title="User Details">
        <SimpleShowLayout>
            <Title title="User Details" />
            
            {/* البيانات النصية */}
            <Box display="flex" gap={2} mb={2} p={2} bgcolor="#f9f9f9" borderRadius={2}>
                <Box flex={1}>
                    <Typography variant="h6">Basic Info</Typography>
                    <TextField source="name" /> <br/>
                    <EmailField source="email" /> <br/>
                    <TextField source="phone" />
                </Box>
                <Box flex={1}>
                    <Typography variant="h6">Identity Text Data</Typography>
                    <Typography variant="body2" color="textSecondary">Extracted/Entered by Admin</Typography>
                    <TextField source="fullName" emptyText="-" label="Full Name" /> <br/>
                    <TextField source="nationalId" emptyText="-" label="National ID" /> <br/>
                    <TextField source="jobTitle" emptyText="-" label="Job Title" /> <br/>
                    <TextField source="maritalStatus" emptyText="-" label="Marital Status" /> <br/>
                    <TextField source="address" emptyText="-" label="Address" />
                </Box>
            </Box>

            <hr style={{ width: '100%', margin: '20px 0' }} />

            <Typography variant="h5" style={{ marginTop: 20, marginBottom: 10 }}>Identity Images</Typography>
            <Typography variant="caption" color="error">Click (X) to permanently delete image from server.</Typography>
            
            <Box display="flex" flexWrap="wrap">
                {/* نمرر fieldName ليتمكن السيرفر من معرفة أي حقل يجب مسحه */}
                <ImageBox title="Profile Picture" source="profileImage" fieldName="profileImage" />
                <ImageBox title="ID Front" source="idFrontImageUrl" fieldName="idFrontImageUrl" />
                <ImageBox title="ID Back" source="idBackImageUrl" fieldName="idBackImageUrl" />
                <ImageBox title="Driving License" source="drivingLicenseUrl" fieldName="drivingLicenseUrl" />
            </Box>

            <hr style={{ width: '100%', margin: '20px 0' }} />

            <Typography variant="h5" style={{ marginTop: 20, marginBottom: 10 }}>Car Details</Typography>

            <FunctionField render={record => {
                if (record.car && record.car.isVerified) {
                    return (
                        <Box>
                            <Box mb={2} p={2} bgcolor="#f5f5f5" borderRadius="5px">
                                <Typography variant="body1"><strong>Model:</strong> {record.car.brand} {record.car.model} ({record.car.year})</Typography>
                                <Typography variant="body1"><strong>Color:</strong> {record.car.color}</Typography>
                                <Typography variant="body1"><strong>Plate:</strong> {record.car.plate}</Typography>
                            </Box>

                            <Box display="flex" flexWrap="wrap">
                                {/* ملاحظة: حالياً زر الحذف يعمل فقط لجدول المستخدمين في الكود أعلاه
                                    لحذف صور السيارة نحتاج لتحديث مشابه للسيارات إذا أردت
                                */}
                                <ImageBox 
                                    title="Car License Front" 
                                    directUrl={record.car.carLicenseFrontUrl} 
                                    // صور السيارة في جدول منفصل، ستحتاج endpoint مختلف إذا أردت مسحها من هنا
                                    // حالياً سأعرضها فقط بدون زر حذف لتجنب الأخطاء، أو يمكنك طلب إضافة كود السيارات
                                />
                                <ImageBox 
                                    title="Car License Back" 
                                    directUrl={record.car.carLicenseBackUrl} 
                                />
                            </Box>
                        </Box>
                    );
                } else {
                    return (
                        <Box p={3} border="1px dashed red" borderRadius="8px" textAlign="center" bgcolor="#fff0f0">
                            <Typography variant="h6" color="error">
                                🚫 No Verified Car
                            </Typography>
                        </Box>
                    );
                }
            }} />

        </SimpleShowLayout>
    </Show>
);